<template>
  <div class="px-4 px-md-0 form-actions text-right">
    <div
      ref="formActions"
      v-sticky="$vuetify.breakpoint.lgAndUp && formObserver.valid && !isContinueTriggered"
      class="form-actions__sticky-holder"
      sticky-side="bottom"
    >
      <div class="holder">
        <!-- PREV STEP BTN -->
        <template v-if="isAdditionalDriver">
          <qz-btn
            key="cancel-editing-btn"
            color="navy-light"
            rounded
            outlined
            x-large
            data-test="cancel_editing_btn"
            @click.prevent="$emit('cancel-driver-editing')"
          >
            Cancel editing
          </qz-btn>
        </template>
        <template v-else>
          <qz-btn
            v-if="currentStep > 1"
            key="previous-btn"
            v-add-zuko-tag="'previous_btn ' + componentKey"
            color="navy-light"
            rounded
            outlined
            x-large
            data-test="previous_btn"
            @click.prevent="$emit('prev-step')"
          >
            <v-icon
              dark
              left
              size="22"
            >
              mdi-arrow-left
            </v-icon>
            Previous
          </qz-btn>
        </template>

        <!-- NEXT STEP/SUBMIT BTN -->
        <div v-show="showActions">
          <template v-if="isLastStep">
            <!-- RECAPTCHA -->
            <template v-if="showCaptcha">
              <vue-recaptcha
                ref="recaptcha"
                size="invisible"
                :sitekey="config.captchaKey"
                :load-recaptcha-script="true"
                @verify="$emit('next-step')"
              />
              <v-btn
                v-add-zuko-tag="'captcha-submit'"
                color="success"
                class="next-step-btn"
                rounded
                x-large
                type="submit"
                data-test="next_btn"
                @click="$emit('captcha-submit')"
              >
                <span>Accept and get quotes</span>
                <v-icon
                  dark
                  right
                  size="22"
                >
                  mdi-arrow-right
                </v-icon>
              </v-btn>
            </template>
            <!-- RECAPTCHA DISABLED -->
            <v-btn
              v-else
              v-add-zuko-tag="'submit'"
              color="success"
              class="next-step-btn"
              rounded
              x-large
              type="submit"
              data-test="next_btn"
              @click.prevent="$emit('next-step')"
            >
              <span>Accept and get quotes</span>
              <v-icon
                dark
                right
                size="22"
              >
                mdi-arrow-right
              </v-icon>
            </v-btn>
          </template>
          <v-btn
            v-else
            v-add-zuko-tag="'next_btn ' + componentKey"
            class="next-step-btn"
            color="success"
            rounded
            x-large
            type="submit"
            data-test="next_btn"
            @click.prevent="$emit('next-step')"
          >
            <template v-if="isAdditionalDriver">
              Save {{ policyholderTypeName }}
            </template>
            <template v-else>
              <span class="d-sm-none">
                Next step
              </span>
              <span class="d-none d-sm-inline">
                Continue to next step
              </span>
            </template>
            <v-icon
              dark
              right
              size="22"
            >
              {{ isAdditionalDriver ? 'mdi-content-save' : 'mdi-arrow-right' }}
            </v-icon>
          </v-btn>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import VueRecaptcha from 'vue-recaptcha';
import {
  ref,
  inject,
  computed,
  onMounted,
  onBeforeUnmount,
} from '@vue/composition-api';

export default {
  name: 'qz-form-actions',
  components: { VueRecaptcha },
  props: {
    isAdditionalDriver: {
      type: Boolean,
      default: false,
    },
    formObserver: {
      type: Object,
      required: true,
    },
    currentStep: {
      type: Number,
      required: true,
    },
    componentKey: {
      type: String,
      required: true,
    },
    showActions: {
      type: Boolean,
      required: true,
    },
    isLastStep: {
      type: Boolean,
      required: true,
    },
    isContinueTriggered: {
      type: Boolean,
      default: false,
    },
  },
  setup () {
    const config = inject('config');
    const productName = inject('productName');
    const recaptcha = ref(null);

    const formActions = ref(null);

    const showCaptcha = computed(() => process.env.NODE_ENV === 'production' ? !config.disableCaptcha : false);
    const policyholderTypeName = productName === 'bike' ? 'rider' : 'driver';

    // scroll focused element into user's workspace
    // (e.g. when form is valid and container with action buttons on fixed position - prevent focused element to appear below this container)
    const fixScroll = async ({ target }) => {
      const isPopupWithOverlay = target.classList.contains('v-dialog__content');
      if (isPopupWithOverlay) return; // prevent page jump on vuetify dialog popup

      const offset = 50; // offset between focused target and container for next/prev buttons
      const isFormActionsFixed = formActions.value && formActions.value.classList.contains('bottom-sticky');
      const formActionsContainer = document.querySelector('.form-actions');

      // don't scroll if from actions isn't fixed or if focus on prev/next buttons
      if (!isFormActionsFixed || formActionsContainer.contains(target)) return;

      const formActionsHeight = formActions.value.getBoundingClientRect().height;

      // proper height calc for custom radio and checkbox items
      const targetType = target.getAttribute('type');
      const isRadioOrCheckbox = ['radio', 'checkbox'].includes(targetType);
      const visibleTargetElement = isRadioOrCheckbox ? target.nextSibling : target;
      const { top, height } = visibleTargetElement.getBoundingClientRect();

      // distance from top of the viewport to the bottom of element's rectangle
      const elementsRectBottomBounding = top + height;

      // browser's viewport height - height of container with action buttons - offset
      const usersLowestWorkspacePoint = window.innerHeight - formActionsHeight - offset;

      const isTargetInUsersWorkspace = elementsRectBottomBounding < usersLowestWorkspacePoint;

      if (!isTargetInUsersWorkspace) {
        const currentScrollPosition = window.scrollY;
        const targetElementRectHiddenAmount = elementsRectBottomBounding - window.innerHeight; // how much element hidden below viewport
        const scrollIntoUsersWorkspaceAmount = currentScrollPosition + targetElementRectHiddenAmount + offset + formActionsHeight;

        window.scrollTo({ top: scrollIntoUsersWorkspaceAmount, behavior: 'smooth' });
      }
    };

    // Lifecycle hooks
    onMounted(() => {
      window.addEventListener('focusin', fixScroll);
    });
    onBeforeUnmount(() => {
      window.removeEventListener('focusin', fixScroll);
    });

    return {
      formActions,
      config,
      showCaptcha,
      policyholderTypeName,
      recaptcha,
    };
  },
};
</script>
